<template>
  <div v-if="hasToolbarMenu" class="toolbar-menu">
    <!-- Desktop: row of toolbar buttons -->
    <div class="toolbar-menu__desktop">
      <ToolbarButton
        v-for="item in sortedItems"
        :key="item.branch_id"
        :item="item"
      />
    </div>

    <!-- Mobile: single overflow button → popup Menu -->
    <div class="toolbar-menu__mobile">
      <div class="toolbar-menu__overflow-wrap">
        <Button
          type="button"
          icon="pi pi-th-large"
          class="toolbar-btn toolbar-menu__overflow-btn"
          :aria-label="overflowAriaLabel"
          :aria-expanded="overflowOpen"
          :title="overflowAriaLabel"
          @click="toggleOverflow"
        />
        <Badge
          v-if="overflowBadgeValue"
          :value="overflowBadgeValue"
          severity="danger"
          class="toolbar-menu__overflow-badge"
        />
      </div>
      <Menu
        ref="overflowMenuRef"
        :model="overflowMenuItems"
        :popup="true"
        @show="overflowOpen = true"
        @hide="overflowOpen = false"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, type ComputedRef } from 'vue'
import { useRouter } from 'vue-router'
import Badge from 'primevue/badge'
import Button from 'primevue/button'
import Menu from 'primevue/menu'
import type { MenuItem as PrimeMenuItem } from 'primevue/menuitem'
import En from '@/common/classes/En'
import { useI18n } from '@/common/composables/useI18n'
import { useMenu } from '@/common/composables/useMenu'
import { EnKey } from '@/components/platform/injection-keys'
import ToolbarButton from './ToolbarButton.vue'
import { navigateMenuItem } from '@/services/menu/menuItemNavigation'
import { useAppStore } from '@/stores/app'
import { useDocumentTabsStore } from '@/stores/documentTabs'
import type { MenuItem } from '@/services/parse/types'

const { t } = useI18n()
const {
  topMenu,
  getItemIcon,
  getItemCounts,
  hasChildren,
  formatToolbarLabel,
} = useMenu()
const router = useRouter()
const appStore = useAppStore()
const documentTabsStore = useDocumentTabsStore()
const en = inject<ComputedRef<En | null>>(EnKey, null)

const overflowMenuRef = ref<InstanceType<typeof Menu> | null>(null)
const overflowOpen = ref(false)

const hasToolbarMenu = computed(() => topMenu.value && topMenu.value.length > 0)

const sortedItems = computed(() => {
  if (!hasToolbarMenu.value) return []

  return [...topMenu.value].sort((a, b) => {
    const sortA = a?.sort ?? 0
    const sortB = b?.sort ?? 0
    return sortA - sortB
  })
})

const navigateTo = async (item: MenuItem) => {
  await navigateMenuItem(item, {
    router,
    appStore,
    documentTabsStore,
    en: en?.value ?? null,
  })
}

function toPrimeMenuItem(item: MenuItem): PrimeMenuItem {
  // Same as desktop ToolbarButton label: "Корзина (3)" / "(3/7)"
  const entry: PrimeMenuItem = {
    label: formatToolbarLabel(item),
    icon: getItemIcon(item),
  }

  if (hasChildren(item) && item.children?.length) {
    entry.items = item.children.map((child) => toPrimeMenuItem(child))
  } else {
    entry.command = () => {
      void navigateTo(item)
    }
  }

  return entry
}

const overflowMenuItems = computed<PrimeMenuItem[]>(() =>
  sortedItems.value.map((item) => toPrimeMenuItem(item)),
)

/** Sum first counters across leaf toolbar items for the overflow trigger badge. */
const overflowBadgeValue = computed(() => {
  let total = 0
  const walk = (items: MenuItem[]) => {
    for (const item of items) {
      if (hasChildren(item) && item.children?.length) {
        walk(item.children)
        continue
      }
      const counts = getItemCounts(item)
      const first = counts?.[0]
      if (typeof first === 'number' && first > 0) total += first
    }
  }
  walk(sortedItems.value)
  return total > 0 ? String(total) : ''
})

const overflowAriaLabel = computed(() => {
  const base = t('platform.toolbarMenu')
  const badge = overflowBadgeValue.value
  return badge ? `${base} (${badge})` : base
})

function toggleOverflow(event: Event) {
  overflowMenuRef.value?.toggle(event)
}
</script>

<style scoped lang="scss">
.toolbar-menu {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px 0 0;
}

.toolbar-menu__desktop {
  display: flex;
  align-items: center;
  gap: inherit;
}

.toolbar-menu__mobile {
  display: none;
  align-items: center;
}

.toolbar-menu__overflow-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.toolbar-menu__overflow-btn {
  color: rgba(255, 255, 255, 0.9) !important;
  background: rgba(255, 255, 255, 0.12) !important;
  border: none !important;
  border-radius: 8px !important;
  width: 38px !important;
  height: 38px !important;
  min-width: 38px !important;
  padding: 0 !important;
}

.toolbar-menu__overflow-btn:hover {
  background: rgba(255, 255, 255, 0.22) !important;
}

.toolbar-menu__overflow-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 1.1rem;
  height: 1.1rem;
  font-size: 0.65rem;
  line-height: 1.1rem;
  pointer-events: none;
  z-index: 1;
}

@media (max-width: 960px) {
  .toolbar-menu {
    gap: 6px;
    padding: 0 12px 0 0;
  }
}

@media (max-width: 768px) {
  .toolbar-menu__desktop {
    display: none;
  }

  .toolbar-menu__mobile {
    display: inline-flex;
  }
}
</style>
